import React, { useCallback, useEffect, useMemo } from 'react';

import { ActionIcon, Text, px, useMantineTheme, Badge, Group, Tooltip } from '@mantine/core';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import {
  IconChevronDown,
  IconChevronUp,
  IconCircleCheckFilled,
  IconCircleOff,
  IconSelector,
  IconTrash,
} from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useDeleteAthleteAppointment from 'hooks/athleteAppointment/useDeleteAthleteAppointment';
import useDeleteAthleteClass from 'hooks/athleteClass/useDeleteAthleteClass';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TablePagination,
  MantineReactTable,
  useMantineReactTable,
} from 'mantine-react-table';
import { AthleteEvent } from 'types';
import { getDayFromDate } from 'utils/event.helpers';
import notificationHandler from 'utils/notifications';

import CreditActionIcon from './CreditActionIcon';
import CoachCell from '../../CoachCell';
import { SearchFilters } from '../types';

interface IAthleteReservationsTableContainerProps {
  searchFilters: SearchFilters;
  tableData: AthleteEvent[];
  setTableData: React.Dispatch<React.SetStateAction<AthleteEvent[]>>;
  enableHook?: boolean;
  permission?: boolean;
  setRowSelection: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>;
  rowSelection: MRT_RowSelectionState;
  removeColumns?: string[];
  sorting: MRT_SortingState;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  pagination: MRT_PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
}

const today = new Date();

const AthleteReservationsTableContainer = ({
  tableData,
  setTableData,
  searchFilters,
  enableHook,
  permission = true,
  setRowSelection,
  rowSelection,
  sorting,
  pagination,
  setSorting,
  setPagination,
  removeColumns = [],
}: IAthleteReservationsTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { dateFormat } = useLocalization();

  const { successNotification, errorNotification } = notificationHandler();
  const deleteAthleteClassMutation = useDeleteAthleteClass();
  const deleteAthleteAppointmentMutation = useDeleteAthleteAppointment();
  const { height } = useElementSize();

  const { data, isError, isLoading } = useAthleteEvents({
    ...searchFilters,
    startDateRange:
      searchFilters.date && searchFilters.date[0]
        ? dayjs(searchFilters.date[0]).format('YYYY-MM-DD')
        : dayjs(today).format('YYYY-MM-DD'),
    endDateRange:
      searchFilters.date && searchFilters.date[1]
        ? dayjs(searchFilters.date[1]).format('YYYY-MM-DD')
        : undefined,
    hideWaitlist: false,
    ordering: sorting[0].desc ? sorting[0].id : `-${sorting[0].id}`,
    enabled: enableHook,
    isBulkAdd: !!searchFilters.isBulkAdd ? searchFilters.isBulkAdd === 'true' : undefined,
    type: 'upcoming',
    offset: `${pagination.pageIndex * pagination.pageSize}`,
    limit: '100',
  });

  const renderAttendedStatus = (row: AthleteEvent): JSX.Element => {
    if (row.isLateCancel) {
      return <Text>Late Cancel</Text>;
    } else if (row.isNoShow) {
      return <Text>No Show</Text>;
    } else if (row.isCheckedIn) {
      return <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />;
    }
    return <IconCircleOff color='gray' />;
  };

  const renderReservedStatus = (row: AthleteEvent): JSX.Element => {
    if (row.isReserved) {
      return (
        <Group noWrap>
          <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />
          {row.isSelfBooked && (
            <Tooltip label='This reservation was made by the athlete'>
              <Badge color='dark'>Self-Booked</Badge>
            </Tooltip>
          )}
        </Group>
      );
    } else if (row.isWaitlisted) {
      return <Text>Waitlist</Text>;
    }
    return <IconCircleOff color='gray' />;
  };

  const handleDeleteAthleteEvent = useCallback(
    (athleteEvent: AthleteEvent): void => {
      const { type, athleteEventId } = athleteEvent;
      if (type === 'athlete_kilo_class') {
        deleteAthleteClassMutation.mutate(athleteEventId, {
          onSuccess: () => {
            successNotification('Attendance record delete success.');
          },
          onError: error => {
            errorNotification('Attendance record delete fail.', error.response?.data.message);
          },
        });
      }
      if (type === 'athlete_appointment') {
        deleteAthleteAppointmentMutation.mutate(athleteEventId, {
          onSuccess: () => {
            successNotification('Attendance record delete success.');
          },
          onError: error => {
            errorNotification('Attendance record delete fail.', error.response?.data.message);
          },
        });
      }
    },
    [deleteAthleteAppointmentMutation, deleteAthleteClassMutation, errorNotification, successNotification],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = useMemo<MRT_ColumnDef<AthleteEvent>[]>(
    () => [
      {
        Header: 'Date',
        accessorKey: 'startDate',
        header: 'startDate',
        size: isDesktop ? 100 : 10,
        Cell: ({ row }) => dayjs(row.original.startDate).format(dateFormat),
      },
      {
        Header: 'Time',
        accessor: 'startTime',
        header: 'startTime',
        size: isDesktop ? 100 : 10,
        Cell: ({ row }) => dayjs(`${row.original.startDate} ${row.original.startTime}`).format('h:mm A'),
      },
      {
        Header: 'Event',
        accessorKey: 'name',
        header: 'name',
        size: isDesktop ? 180 : 10,
        Cell: ({ row }) => row.original.name,
      },
      {
        Header: 'Coach',
        accessorKey: 'coachName',
        header: 'coachName',
        size: isDesktop ? 160 : 10,
        Cell: ({ row }) => <CoachCell coachName={row.original.coachName} />,
      },
      {
        Header: 'Reserved',
        accessor: 'isReserved',
        header: 'isReservedOrWaitlisted',
        size: 10,
        Cell: ({ row }) => renderReservedStatus(row.original),
      },
      {
        Header: 'Attended',
        accessor: ['isReserved', 'isWaitlisted'],
        header: 'isCheckedIn',
        size: 10,
        Cell: ({ row }) => renderAttendedStatus(row.original),
      },
      {
        Header: 'Weekday',
        header: 'weekday',
        id: 'weekday',
        size: isDesktop ? 180 : 10,
        Cell: ({ row }) => getDayFromDate(row.original.startDate),
      },
      {
        Header: 'Bulk Added',
        accessor: 'isBulkAdd',
        header: 'isBulkAdd',
        size: 10,
        Cell: ({ row }) =>
          row.original.isBulkAdd ? (
            <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />
          ) : (
            <IconCircleOff color='gray' />
          ),
      },
      {
        Header: 'Credited',
        header: 'isCredited',
        size: 10,
        Cell: ({ row }) => <CreditActionIcon athleteEvent={row.original} />,
      },
      {
        id: 'action',
        Header: 'Delete',
        header: 'action',
        size: 10,
        Cell: ({ row }) =>
          row.original.isImported ? (
            <></>
          ) : (
            <ActionIcon
              disabled={!permission}
              color='red'
              onClick={() => handleDeleteAthleteEvent(row.original)}
            >
              <IconTrash />
            </ActionIcon>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDeleteAthleteEvent, permission, theme.colors.kiloBlue, isDesktop],
  );

  const table = useMantineReactTable({
    columns: columns.filter(c => isDesktop || !removeColumns.includes(c.header || '')),
    data: tableData,
    enableBottomToolbar: false,
    enableRowSelection: permission,
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      showGlobalFilter: true,
      sorting: [{ id: 'startDate', desc: false }],
    },
    enableStickyHeader: true,
    mantinePaginationProps: {
      rowsPerPageOptions: ['100'],
      showRowsPerPage: false,
    },
    mantineTableContainerProps: {
      sx: isDesktop
        ? { height: `calc(100vh - ${height + px(theme.spacing.lg) * 11}px)` }
        : { width: '100vw' },
    },

    getRowId: row => row.athleteEventId,
    // mantineTableHeadCellProps: {
    //   sx: {
    //     direction: 'rtl',
    //     justifyContent: 'end',
    //   },
    // },
    icons: {
      IconArrowsSort: () => {
        return <IconSelector size={theme.fontSizes.sm} color='black' />;
      },
      IconSortAscending: () => {
        return <IconChevronUp size={theme.fontSizes.sm} color='black' />;
      },
      IconSortDescending: () => {
        return <IconChevronDown size={theme.fontSizes.sm} color='black' />;
      },
    },
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    maxMultiSortColCount: 1,
    enableSorting: true,
    enablePagination: true,
    enableMultiRowSelection: permission,
    rowCount: data?.pages?.[0]?.data?.count ?? 0,
    paginationDisplayMode: 'pages',
    enableSelectAll: true,
    enableColumnActions: false,
    onSortingChange: setSorting,
    enableDensityToggle: false,
    enableExpanding: false,
    enableExpandAll: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableTableFooter: false,
    enableTopToolbar: false,
    manualFiltering: true,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: 'Error loading data',
        }
      : undefined,
    onPaginationChange: setPagination,
    state: {
      sorting,
      isLoading,
      pagination,
      showAlertBanner: isError,
      rowSelection,
    },
  });

  return (
    <>
      <MantineReactTable table={table} />
      <MRT_TablePagination table={table} />
    </>
  );
};

export default AthleteReservationsTableContainer;
